declare global {
  interface Window {
    grecaptcha: any;
  }
}

import React, { useState, useEffect } from 'react';
import Script from 'next/script';
import { isClientSide } from 'utils/host-config';

interface UseRecaptchaReturn {
  scriptLoaded: boolean;
  RecaptchaScript: React.ReactElement | null;
}

/**
 * Custom hook for managing reCAPTCHA script loading
 * Handles script loading state, error handling, and provides the Script component
 * 
 * @returns Object containing scriptLoaded state and RecaptchaScript component
 */
export const useRecaptcha = (): UseRecaptchaReturn => {
  const [scriptLoaded, setScriptLoaded] = useState(false); // Always start as false for safety
  const [scriptError, setScriptError] = useState(false);

  // Check for existing reCAPTCHA on component mount
  useEffect(() => {
    if (isClientSide() && window.grecaptcha && window.grecaptcha.enterprise) {
      // console.log('reCAPTCHA already loaded, setting scriptLoaded to true');
      setScriptLoaded(true);
    }
  }, []);

  const handleScriptLoad = () => {
    setScriptLoaded(true);
  };

  const handleScriptError = (error: Error) => {
    console.error('Failed to load reCAPTCHA script:', error);
    setScriptError(true);
  };

  const RecaptchaScript = !scriptLoaded && !scriptError ? (
    <Script 
      src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_KEY}`}
      onLoad={handleScriptLoad}
      onError={handleScriptError}
    />
  ) : null;

  return {
    scriptLoaded,
    RecaptchaScript,
  };
};

export default useRecaptcha;
